'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useWriteTransition } from '@/lib/refresh-gate';
import { reach } from '@/lib/reach';
import { childHref } from '@/lib/trail';
import { extractHashtags } from '@/domain/item-tags';
import { createItem } from '@/app/actions';
import { Button, Input, type ButtonVariant, useOverlayBusy } from '@/components/ui';
import { Notice } from '@/components/ui/notice';
import { mintCreateKey, useRetryKey } from './create-key';
import { HashtagSuggest, useHashtagAutocomplete } from './hashtag-autocomplete';
import {
  addCreate,
  failCreate,
  resendCreate,
  settleCreate,
  usePendingCreates,
} from './pending-creates';
import { TapLink } from './tap-link';
import { errorMessage } from '@/domain/types';
import { useT } from '@/lib/locale-context';
import styles from './create-task.module.css';

/**
 * Create a contract item (docs/CONTRACT_SPINE.md D9). The creator becomes the
 * requester automatically; the task lands unclaimed "needs doing" until someone
 * claims it. Placement is optional (birth recipe runs server-side):
 *   - `boardId`: create on a named board (grant to scope group + pin if curated).
 *   - `groupId`: create granted to a group (no board).
 *   - neither: floating item, appears in "Ungrouped" on home.
 * `bornActive`: when the caller is the active axis's Active lane, the birth
 *   declares the item active (via the mapped act, server-side) so the new item
 *   appears among its siblings immediately (birth recipe invariant, §7).
 * `flowPhaseKey`: when the caller is a STAGE lane (a flow-linked board, docs/FLOW.md),
 *   the birth recipe joins the flow at that stage and folds its `means` atom
 *   pairs into the birth standings - the new item appears in this lane
 *   immediately, same invariant.
 * `optimistic`: the Work list's compose bar (docs/LOADING.md §2 tool 7, §3.4).
 *   Enter clears the field at once and keeps its focus (the keyboard stays up
 *   on a phone); the new row lands in the list before the reply, inert until
 *   the reply names the id (`src/components/pending-creates.ts`, drawn by the
 *   lens surface); a refusal or a network failure marks it "Not saved. Retry",
 *   and the retry resends the create under the same key. A new item the
 *   list's criteria do not take lands in no row, so one quiet line under the
 *   field says so, with a link to the item, until the next submit. Without
 *   it the bar waits for the reply (a dialog, a board's lane), since no list
 *   on that screen draws the entry.
 */
export function CreateTask({
  boardId,
  groupId,
  parentItemId,
  bornActive,
  flowPhaseKey,
  label,
  placeholder,
  buttonVariant = 'accent',
  autoFocus = false,
  optimistic = false,
  onCreated,
}: {
  boardId?: string;
  groupId?: string;
  parentItemId?: string;
  /** Declare the newborn active (the Active lane's composer only). */
  bornActive?: boolean;
  /** Stage lane to join at birth (board stage lanes only, with `boardId`). */
  flowPhaseKey?: string;
  /** The field's name and the button's word ("New item" by default). */
  label?: string;
  /** The field's hint ("Add an item" by default). */
  placeholder?: string;
  /** The submit's weight: `accent` inline (default); `solid` in the compose bar. */
  buttonVariant?: ButtonVariant;
  /** Focus the input on mount (the compose bar opens ready to type). */
  autoFocus?: boolean;
  /** Draw the create in the list before the reply (see above). */
  optimistic?: boolean;
  /** After a successful create (e.g. close the hosting compose bar). Not
   *  called in `optimistic` mode: that bar stays open for the next line. */
  onCreated?: () => void;
}) {
  const t = useT();
  const words = label ?? t('create.task.label');
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, start] = useWriteTransition();
  // An optimistic create shows its result at once and stays open for
  // the next line, so only the plain create holds the overlay around it.
  useOverlayBusy(pending && !optimistic);
  // Typing `#` offers the viewer's tags inline (docs/FIELDS_AND_TAGS.md §6.3).
  const {
    inputRef,
    floatingRef,
    suggest,
    fieldProps,
    onChange: onType,
    onSelect,
    onKeyDown: onSuggestKey,
  } = useHashtagAutocomplete({ value: title, setValue: setTitle });
  const createKey = useRetryKey();
  const refocus = useRef(false);
  useEffect(() => {
    if (!pending && refocus.current) {
      refocus.current = false;
      inputRef.current?.focus();
    }
  }, [pending, inputRef]);

  // The optimistic bar's latest create, read back from the store for the one
  // line under the field (a failure, or an item the list does not show).
  const creates = usePendingCreates();
  const [lastKey, setLastKey] = useState<string | null>(null);
  // Held by a hook: the retry below closes over it (the compiler aliasing
  // trap - a plain call's result read in an escaping closure).
  const last = useMemo(
    () => (lastKey === null ? undefined : creates.find((c) => c.key === lastKey)),
    [creates, lastKey],
  );
  const pathname = usePathname();
  // The latest create's state as this bar saw it, kept here rather than read
  // from the store: the store lets an entry go the moment its row arrives,
  // which is the same render as the reply.
  const [lastState, setLastState] = useState<{
    key: string;
    state: 'saving' | 'saved' | 'failed';
  } | null>(null);

  function submitOptimistic(typed: string) {
    // Minted at the gesture; every retry of this entry resends it
    // (docs/LOADING.md §3.4), so a lost reply never makes a second item.
    const key = mintCreateKey();
    const placement = { boardId, groupId, parentItemId, bornActive, flowPhaseKey };
    const mark = (state: 'saving' | 'saved' | 'failed') =>
      setLastState((prev) => (prev?.key === key ? { key, state } : prev));
    const send = (begin: typeof start) => {
      resendCreate(key);
      mark('saving');
      begin(async () => {
        const res = await reach(() => createItem({ ...placement, title: typed, key }));
        if (res.ok) settleCreate(key, res.id);
        else failCreate(key, t.msg(errorMessage(res.error)));
        mark(res.ok ? 'saved' : 'failed');
      });
    };
    addCreate({ key, typed, send });
    setLastKey(key);
    setLastState({ key, state: 'saving' });
    setTitle('');
    setError(null);
    send(start);
    // The field never left the enabled state, so it keeps its focus; the
    // explicit focus covers a submit by the button.
    inputRef.current?.focus();
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const typed = title.trim();
    // An empty submit is not an error: guide the user to the input rather than
    // sitting dead. The button stays alive so it never reads as "not clickable".
    if (typed.length === 0) {
      inputRef.current?.focus();
      return;
    }
    if (optimistic) {
      submitOptimistic(typed);
      return;
    }
    start(async () => {
      try {
        const res = await createItem({
          boardId,
          groupId,
          title: typed,
          parentItemId,
          bornActive,
          flowPhaseKey,
          key: createKey.keyFor(typed),
        });
        if (res.ok) {
          createKey.settle();
          setTitle('');
          setError(null);
          // The next line, without reaching for the field again
          // (docs/CHECKLIST.md §3.2: Enter adds and keeps the keyboard up):
          // the input is disabled while pending, which drops focus, so it is
          // taken back once the transition settles (the effect below).
          refocus.current = true;
          onCreated?.();
        } else {
          setError(t.msg(errorMessage(res.error)));
        }
      } catch {
        // A rejected action (e.g. the request never reached the server) must not
        // vanish: surface it instead of silently swallowing the create.
        setError(t('create.error.unreachable'));
      }
    });
  }

  return (
    <form
      className={styles.form}
      method="post"
      onSubmit={submit}
      data-testid="create-task"
      // The latest create's state, for a reader that must wait for the write
      // (e2e/home-create.ts); nothing is drawn from it.
      data-last-create={lastState?.state}
    >
      <Input
        ref={inputRef}
        className={styles.grow}
        value={title}
        onChange={onType}
        onSelect={onSelect}
        onKeyDown={onSuggestKey}
        placeholder={placeholder ?? t('create.task.placeholder')}
        aria-label={words}
        autoFocus={autoFocus}
        disabled={pending && !optimistic}
        {...fieldProps}
      />
      <HashtagSuggest suggest={suggest} floatingRef={floatingRef} />
      <Button type="submit" variant={buttonVariant} pending={pending && !optimistic}>
        {words}
      </Button>
      {error && <span className={styles.error}>{error}</span>}
      {optimistic && last && !last.drawn && (
        <CreateNote
          entry={last}
          href={last.serverId ? childHref(last.serverId, pathname, []) : null}
          retry={() => last.send(start)}
        />
      )}
    </form>
  );
}

/**
 * The compose bar's one line about a create no list on the screen draws
 * (docs/LOADING.md §3.4, question 6 of §10): a failure with its retry, or an
 * item the list's criteria do not take, named with a link once the reply
 * names it. Quiet text, not alarm-coloured, for the second: nothing failed.
 */
function CreateNote({
  entry,
  href,
  retry,
}: {
  entry: { typed: string; error: string | null; unmatched: boolean };
  href: string | null;
  retry: () => void;
}) {
  const t = useT();
  const title = extractHashtags(entry.typed).text || entry.typed;
  if (entry.error) {
    return (
      <Notice block className={styles.note} testId="create-note">
        {t.rich('create.task.failed', {
          title,
          retry: (
            <button
              key="retry"
              type="button"
              className={styles.retry}
              title={entry.error}
              onClick={retry}
              data-testid="create-retry"
            >
              {t('create.task.retry')}
            </button>
          ),
        })}
      </Notice>
    );
  }
  if (!entry.unmatched) return null;
  return (
    <p className={`${styles.note} ${styles.quiet}`} role="status" data-testid="create-note">
      {t.rich('create.task.unmatched', {
        title: href ? (
          <TapLink key="title" href={href} tap={{ title }} className={styles.noteLink}>
            {title}
          </TapLink>
        ) : (
          <span key="title">{title}</span>
        ),
      })}
    </p>
  );
}
